import Link from 'next/link'
import type { ReactNode } from 'react'

type PagerProps = {
  /** 表示中のページ（1 始まり） */
  current: number
  totalPages: number
  /** ページ番号から URL を作る。渡すとリンクで描画する（ページごとにルートがある一覧） */
  hrefFor?: (page: number) => string
  /** 渡すとボタンで描画する（絞り込み結果をクライアント側で送る場合） */
  onChange?: (page: number) => void
}

type PagerItem = number | 'ellipsis'

/**
 * 表示するマス。先頭・末尾・表示中の前後 1 ページを出し、飛んだところを「…」にする。
 * 1 ページだけ飛ぶ場合は「…」ではなく番号を出す（マスの数が同じで情報が多いため）
 */
const getPagerItems = (current: number, totalPages: number): PagerItem[] => {
  const pages = [...new Set([1, current - 1, current, current + 1, totalPages])]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b)

  return pages.flatMap((page, index): PagerItem[] => {
    const prev = pages[index - 1]
    if (prev === undefined || page - prev === 1) return [page]
    return page - prev === 2 ? [prev + 1, page] : ['ellipsis', page]
  })
}

const cellClasses =
  'flex size-10 items-center justify-center rounded-lg text-sm leading-compact'

const focusClasses =
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-line-inverse'

const numberClasses = `${cellClasses} ${focusClasses} border border-line bg-surface text-ink transition-colors duration-120 hover:border-line-inverse hover:bg-surface-inverse hover:text-ink-inverse`

const arrowClasses = `${cellClasses} ${focusClasses} text-ink transition-opacity hover:opacity-60`

/**
 * ページャ（Figma pager）。40×40 / radius sm のマスを 8 ずつ空けて並べる。
 * number は白地＋枠、current は黒地＋白文字、ellipsis は面なし、prev / next は Chevron 40px。
 */
export function Pager({ current, totalPages, hrefFor, onChange }: PagerProps) {
  if (totalPages <= 1) return null

  const control = (
    page: number,
    className: string,
    children: ReactNode,
    label?: string
  ) =>
    hrefFor ? (
      <Link href={hrefFor(page)} className={className} aria-label={label}>
        {children}
      </Link>
    ) : (
      <button
        type="button"
        onClick={() => onChange?.(page)}
        className={className}
        aria-label={label}
      >
        {children}
      </button>
    )

  const arrow = (direction: 'prev' | 'next') => {
    const page = direction === 'prev' ? current - 1 : current + 1
    const icon = (
      <span
        aria-hidden="true"
        className={`icon size-10 ${direction === 'prev' ? 'icon-chevron-left' : 'icon-chevron-right'}`}
      />
    )
    // 端では押せないので隠す。マスは残して番号の位置を揃える
    if (page < 1 || page > totalPages) {
      return (
        <li aria-hidden="true" className="invisible">
          <span className={cellClasses}>{icon}</span>
        </li>
      )
    }
    return (
      <li>
        {control(
          page,
          arrowClasses,
          icon,
          direction === 'prev' ? '前のページ' : '次のページ'
        )}
      </li>
    )
  }

  return (
    <nav aria-label="ページ送り">
      <ul className="flex flex-wrap items-center justify-center gap-2">
        {arrow('prev')}
        {getPagerItems(current, totalPages).map((item, index) => {
          if (item === 'ellipsis') {
            return (
              // 「…」は同じ行に 2 つ出ることがあるので位置で鍵を作る
              // biome-ignore lint/suspicious/noArrayIndexKey: 「…」は値を持たない
              <li key={`ellipsis-${index}`}>
                <span className={`${cellClasses} text-ink-subtle`}>…</span>
              </li>
            )
          }
          return (
            <li key={item}>
              {item === current ? (
                <span
                  aria-current="page"
                  className={`${cellClasses} bg-surface-inverse text-ink-inverse`}
                >
                  {item}
                </span>
              ) : (
                control(item, numberClasses, item, `${item} ページ目`)
              )}
            </li>
          )
        })}
        {arrow('next')}
      </ul>
    </nav>
  )
}
